/** Returns option values restated as literal lists so `redo/settings/common`
 * takes no build dependency on the returns packages — same as `support/`. */

export interface ReturnsSettingOption<Value extends string = string> {
  value: Value;
  label: string;
}

/** Mirrors `ExchangeOrderLineItemPriceSource`. */
export const RETURNS_EXCHANGE_ORDER_LINE_ITEM_PRICE_SOURCE_OPTIONS = [
  { value: "discountedPrice", label: "Use discounted price" },
  { value: "price", label: "Use full retail price" },
] as const satisfies readonly ReturnsSettingOption[];

export function returnsSettingOptionValues<Value extends string>(
  options: readonly ReturnsSettingOption<Value>[],
): [Value, ...Value[]] {
  const [first, ...rest] = options.map((option) => option.value);
  if (first === undefined) {
    throw new Error("returns setting option list must not be empty");
  }
  return [first, ...rest];
}

/** Mirrors `ShopAgainAction` — where store credit is issued on processing. */
export const RETURNS_STORE_CREDIT_TYPE_OPTIONS = [
  { value: "gift_card", label: "Gift card" },
  { value: "discount_code", label: "Discount code" },
  { value: "rise", label: "Rise credit" },
  { value: "loopz", label: "Loopz" },
  { value: "bubblehouse", label: "Bubblehouse" },
  { value: "shopify_account", label: "Shopify account" },
  { value: "creditsyard", label: "CreditsYard" },
  { value: "gift_card_order", label: "Gift card order" },
  { value: "noop", label: "No action" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `RefundDutiesMode`. */
export const RETURNS_REFUND_DUTIES_MODE_OPTIONS = [
  { value: "off", label: "Off" },
  { value: "all", label: "All customers" },
  { value: "covered_only", label: "Only customers who purchased coverage" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `MerchantCreatedReturnLabelChargeDefault`. */
export const RETURNS_MERCHANT_CREATED_RETURN_LABEL_CHARGE_DEFAULT_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors the restock choices on the Automations inventory card. */
export const RETURNS_RESTOCK_OPTIONS = [
  { value: "all", label: "All returned items" },
  { value: "not_damaged", label: "Only items that aren't damaged" },
  { value: "none", label: "Never restock automatically" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors the `ReturnStatus` subset a return can be auto-processed at. */
export const RETURNS_PROCESS_AT_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_transit", label: "In transit" },
  { value: "delivered", label: "Delivered" },
  { value: "complete", label: "Complete" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `LabelExpirationStrategy`. */
export const RETURNS_LABEL_EXPIRATION_STRATEGY_OPTIONS = [
  { value: "return", label: "Return date" },
  { value: "order", label: "Order date" },
  { value: "delivery", label: "Delivery date" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `CompensationMethod`. */
export const RETURNS_COMPENSATION_METHOD_OPTIONS = [
  { value: "refund", label: "Refund" },
  { value: "store_credit", label: "Store credit" },
  { value: "exchange", label: "Exchange" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `ReturnTypeEnum`. */
export const RETURNS_RETURN_TYPE_OPTIONS = [
  { value: "claim", label: "Claim" },
  { value: "return", label: "Return" },
  { value: "warranty", label: "Warranty" },
  { value: "recycle", label: "Recycle" },
  { value: "withdrawal", label: "Withdrawal" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `AgentReviewRiskLevel`. */
export const RETURNS_AGENT_REVIEW_RISK_LEVEL_OPTIONS = [
  { value: "low", label: "Low risk" },
  { value: "medium", label: "Medium risk" },
  { value: "high", label: "High risk" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `AgentReviewAction`. */
export const RETURNS_AGENT_REVIEW_ACTION_OPTIONS = [
  { value: "approve", label: "Approve" },
  { value: "deny", label: "Deny" },
  { value: "flag", label: "Flag for review" },
  { value: "none", label: "No action" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `closeShopifyReturnOptions`. */
export const RETURNS_CLOSE_SHOPIFY_RETURN_OPTIONS = [
  { value: "enabled", label: "Close when processed" },
  { value: "greenReturnsOnly", label: "Green returns only" },
  { value: "disabled", label: "Never close" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `GiftCardRecipientEnum`. */
export const RETURNS_GIFT_CARD_RECIPIENT_OPTIONS = [
  { value: "recipient", label: "Gift recipient" },
  { value: "customer", label: "Original customer" },
  { value: "both", label: "Both" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `BARCODE_HEADER_FOOTER_OPTIONS`. */
export const RETURNS_BARCODE_TEXT_OPTIONS = [
  { value: "upc", label: "UPC" },
  { value: "product-variant-name", label: "Product and variant title" },
  { value: "product-over-sku", label: "Product title and sku" },
  { value: "sku-and-warehouse", label: "Sku and location" },
  { value: "sku-only", label: "Sku only" },
  { value: "warehouse-only", label: "Location only" },
  { value: "none", label: "None" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `RefundType` for leftover exchange value. */
export const RETURNS_EXCESS_EXCHANGE_VALUE_OPTIONS = [
  { value: "store_credit", label: "Issue store credit" },
  { value: "refund", label: "Refund the difference" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `OutOfStockAdvanced`. */
export const RETURNS_OUT_OF_STOCK_ADVANCED_OPTIONS = [
  { value: "cancel", label: "Cancel the entire exchange order" },
  { value: "continue", label: "Continue and fulfill what is in stock" },
  { value: "manual", label: "Pause until you resolve it" },
  { value: "oversell", label: "Oversell the item" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `ExchangeCreateTrigger`. */
export const RETURNS_EXCHANGE_CREATE_TRIGGER_OPTIONS = [
  { value: "creation", label: "When the return is created" },
  { value: "creation_upsells", label: "When the return is created (upsells)" },
  { value: "return_approved", label: "When the return is approved" },
  {
    value: "exchange_products_processed",
    label: "When exchange products are processed",
  },
  { value: "all_products_processed", label: "When all products are processed" },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `ExchangeFinancialStrategy`. `none` is the pre-migration fallback:
 * still stored on some teams, but the pricing card does not offer it. */
export const RETURNS_EXCHANGE_FINANCIAL_STRATEGY_OPTIONS = [
  { value: "none", label: "Legacy strategy" },
  { value: "upsell_value", label: "Use only the upsell amount" },
  { value: "full_price", label: "Use discounted item prices" },
  {
    value: "full_price_discount_to_upsell_value",
    label: "Use discounted item prices, then apply an order discount",
  },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `NonZeroValueExchangeStrategy`. */
export const RETURNS_NON_ZERO_VALUE_EXCHANGE_STRATEGY_OPTIONS = [
  { value: "none", label: "None" },
  { value: "apply_discount", label: "Apply discount" },
  { value: "mark_as_paid", label: "Mark as paid" },
  {
    value: "discounted_value_marked_as_paid",
    label: "Discounted value marked as paid",
  },
  {
    value: "create_and_complete_draft_order",
    label: "Create and complete draft order",
  },
  {
    value: "upsell_value_marked_as_paid",
    label: "Upsell value marked as paid",
  },
  {
    value: "apply_exchange_discount_with_tax_mark_as_paid",
    label: "Apply exchange discount with tax, mark as paid",
  },
] as const satisfies readonly ReturnsSettingOption[];

/** Mirrors `EXCHANGE_ORDER_PREFIX_TYPES` (return types plus managed claims). */
export const RETURNS_EXCHANGE_ORDER_PREFIX_TYPE_OPTIONS = [
  { value: "claim", label: "Claim" },
  { value: "return", label: "Return" },
  { value: "warranty", label: "Warranty" },
  { value: "recycle", label: "Recycle" },
  { value: "withdrawal", label: "Withdrawal" },
  { value: "managed_claim", label: "Managed claim" },
] as const satisfies readonly ReturnsSettingOption[];
